import { ctaTokenId } from "@/assets/googleTrackIds";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import {
  Button,
  DataHeader,
  Flex,
  LegacyCardList,
  Modal,
  Space,
  TableAction,
  Tag,
  Typography,
  useMessagedAsync,
  usePaypingContext,
} from "@payping/web-components";
import { useState } from "react";
import { TokenType } from "@/pages/Connection/Developers/Token/TokenTypes";
import { ConfirmModal, ConfirmModalProps } from "@/components/DashIpg/ConfirmModal";
import { deleteClientAppOrToken, getClientTokenList } from "@payping/redux/actions";
import { dateFormats, jalaliNow, toJalali, toUtc } from "@payping/utils/dateUtils";
import { DisplayModeEnum } from "@payping/web-components/legacyComponents/List/types";
import type { ListCardProps } from "@payping/web-components/legacyComponents/List/types";
import { Add, Delete, Edit, FiClock, PhRCheckCircle, PhRxCircle, SwapOutlined, Website } from "@payping/icons";
import moment from "jalali-moment";
import { TokenFormModal } from "@/pages/Connection/Developers/Token/TokenFormModal";
import TokenRenewModal from "@/pages/Connection/Developers/Token/TokenRenewModal";
import { Link } from "react-router-dom";
import { DashboardRoutes } from "@/router";
import { TokenListEmpty } from "./TokenListEmpty";
import { useAtom } from "jotai";
import { selectAtom } from "jotai/utils";
import { dashboardPreferencesAtom } from "@/core/state";

const languageAtom = selectAtom(dashboardPreferencesAtom, (val) => val.language);

const TokensSummery = () => {
  const dispatch = useAppDispatch();
  const { breakpoints } = usePaypingContext();
  const { lg } = breakpoints;
  const [language] = useAtom(languageAtom);

  const tokenList = useAppSelector((state) => state?.client?.token?.list);

  const [formModalOpen, setFormModalOpen] = useState<boolean>(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [renewModalOpen, setRenewModalOpen] = useState<boolean>(false);
  const initialTokenValues: TokenType = {
    isTest: false,
    scopes: ["pay:read", "pay:write"],
  };
  const [formModalData, setFormModalData] = useState<TokenType>(initialTokenValues);
  const [confirmModalData, setConfirmModalData] = useState<ConfirmModalProps>();
  const [renewModalClientId, setRenewModalClientId] = useState<string>("");
  const { t } = useAppTranslation();
  const [modal, contextHolder] = Modal.useModal();

  const handleDelete = (clientId: string) => {
    if (tokenList) {
      modal.confirm({
        cancelText: t("cancel"),
        content: t("token.remove.confirm"),
        mask: { closable: true },
        okText: t("ok"),
        onOk: async () => {
          dispatch(deleteClientAppOrToken(clientId as string));
        },
      });
    }
  };

  function handleRenew(clientId: string) {
    setRenewModalClientId(clientId);
    setRenewModalOpen(true);
  }
  function showModal(token?: TokenType) {
    if (token) {
      setFormModalData({
        ...token,
        expireDate: toUtc({
          date: token.expireDate,
          dateFormat: dateFormats.standard.utcDateTime,
          toFormat: dateFormats.ui.utcDate,
        }).object,
      });
    } else {
      setFormModalData(initialTokenValues);
    }
    setFormModalOpen(true);
  }

  const data: ListCardProps["dataSource"] = (tokenList || []).slice(0, 3).map((token) => ({
    key: token.clientId || "",
    actions: [
      <a key={0}>
        <TableAction Icon={Edit} iconColor={"cta"} onClick={() => showModal(token)} label={t("edit")} />
      </a>,
      <a key={1}>
        <TableAction
          key={1}
          Icon={SwapOutlined}
          iconColor={"primaryLite"}
          onClick={() => handleRenew(token.clientId || "")}
          label={t("token.renew.renewToken")}
        />
      </a>,
      <a key={1}>
        <TableAction
          Icon={Delete}
          iconColor={"danger"}
          label={t("delete")}
          onClick={(): void => handleDelete(token.clientId || "")}
          key={2}
        />
      </a>,
    ],
    descriptions: (
      <Flex gap={"small"}>
        <Website width={14} color={"black"} />
        <Typography.Text>
          {t("token.websiteAddress")} {token.clientUri}
        </Typography.Text>
      </Flex>
    ),
    content: !lg
      ? [
          <>
            {(() => {
              const expireMoment = moment(
                toJalali({
                  date: token.expireDate,
                  toFormat: dateFormats.ui.jalaliDateTime,
                }).string,
                dateFormats.ui.jalaliDateTime,
              );
              const nowMoment = moment(jalaliNow().string, dateFormats.ui.jalaliDateTime);
              const isExpired = expireMoment.isBefore(nowMoment);

              if (isExpired) {
                return (
                  <Tag color={"orange"} icon={<FiClock width={14} />}>
                    {t("coupon.status.EXPIRED")}
                  </Tag>
                );
              }

              // @ts-expect-error missing isVerify in type
              return token.isVerify ? (
                <Tag color={"green"} icon={<PhRCheckCircle width={14} />}>
                  {t("overview.completeProfile.status.confirmed")}
                </Tag>
              ) : (
                <Tag color={"red"} icon={<PhRxCircle width={14} />}>
                  {t("unverified")}
                </Tag>
              );
            })()}
            {token.isTest ? <Tag color={"blue"}>{t("token.testToken")}</Tag> : null}
          </>,
        ]
      : [],
    title: {
      primary: (
        <Space>
          <Typography.Title level={5} className={"tw:m-0! tw:font-normal"}>
            {token.clientName}
          </Typography.Title>
          <Typography.Text type={"secondary"}>
            {t("expire")}
            {moment
              .duration(
                toJalali({ date: token.expireDate, toFormat: dateFormats.ui.jalaliDateTime }).object?.diff(
                  jalaliNow().object,
                ),
              )
              .locale(language)
              .humanize(true)}
          </Typography.Text>
          {lg ? (
            <>
              {(() => {
                const expireMoment = moment(
                  toJalali({
                    date: token.expireDate,
                    toFormat: dateFormats.ui.jalaliDateTime,
                  }).string,
                  dateFormats.ui.jalaliDateTime,
                );
                const nowMoment = moment(jalaliNow().string, dateFormats.ui.jalaliDateTime);
                const isExpired = expireMoment.isBefore(nowMoment);

                if (isExpired) {
                  return (
                    <Tag color={"orange"} icon={<FiClock width={14} />}>
                      {t("coupon.status.EXPIRED")}
                    </Tag>
                  );
                }

                // @ts-expect-error missing isVerify in type
                return token.isVerify ? (
                  <Tag color={"green"} icon={<PhRCheckCircle width={14} />}>
                    {t("overview.completeProfile.status.confirmed")}
                  </Tag>
                ) : (
                  <Tag color={"red"} icon={<PhRxCircle width={14} />}>
                    {t("unverified")}
                  </Tag>
                );
              })()}
              {token.isTest ? <Tag color={"blue"}>{t("token.testToken")}</Tag> : null}
            </>
          ) : null}
        </Space>
      ),
    },
  }));

  // TODO: getClientTokenList method doesn't accept offset and limit for pagination
  const { loading } = useMessagedAsync(() => dispatch(getClientTokenList()), [dispatch], { useProgress: true });

  return (
    <>
      <Space orientation={"vertical"} size={"middle"} className={"tw:w-full"}>
        <DataHeader
          title={<Typography.Title level={4}>{t("pageTitles.token")}</Typography.Title>}
          description={<Typography.Text>{t("connections.token.subTitle")}</Typography.Text>}
          primaryAction={
            <>
              {!loading && (tokenList || [])?.length === 0 ? <TokenListEmpty showModal={showModal} /> : null}
              {loading || tokenList?.length > 0 ? (
                <Button
                  key={0}
                  type={"primary"}
                  loading={loading}
                  icon={<Add width={14} height={14} />}
                  onClick={() => showModal()}
                  data-google-track={ctaTokenId}
                >
                  {t("token.addToken")}
                </Button>
              ) : null}
            </>
          }
        />

        {loading || tokenList?.length > 0 ? (
          <Flex vertical={true} gap={"16px"}>
            <LegacyCardList
              className={"tw:w-full"}
              loading={loading}
              grid={{ xs: 1, sm: 1, md: 1, lg: 3, xl: 4, xxl: 4, xxxl: 4 }}
              dataSource={data}
              mode={lg ? DisplayModeEnum.List : DisplayModeEnum.Card}
            />
            {tokenList?.length > 2 ? (
              <Flex justify={"center"}>
                <Link to={DashboardRoutes.connections.developers.tokensList.url}>
                  {t("connections.token.showMore")}
                </Link>
              </Flex>
            ) : null}
          </Flex>
        ) : null}
      </Space>

      <TokenFormModal
        open={formModalOpen}
        setOpen={setFormModalOpen}
        setConfirmModalOpen={setConfirmModalOpen}
        formModalData={formModalData}
        setConfirmModalData={setConfirmModalData}
        modal={modal}
      />
      <TokenRenewModal
        open={renewModalOpen}
        setOpen={setRenewModalOpen}
        setConfirmModalOpen={setConfirmModalOpen}
        setConfirmModalData={setConfirmModalData}
        clientId={renewModalClientId}
      />
      <ConfirmModal open={confirmModalOpen} setOpen={setConfirmModalOpen} {...confirmModalData} />
      {contextHolder}
    </>
  );
};

export default TokensSummery;
